import { AxiosError, AxiosResponse } from 'axios'

import { User, UserPatch } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { submitEditProfileData } from './editProfile'
import { sentryCaptureException } from '../../utils/sentryUtils'
import { axiosInstance } from '../axiosInstance'

/**
 * ACCOUNT-2945 — single entry point for the incremental cutover of the legacy PUT /v1/user route to
 * the unified PATCH /v2/user endpoint (mirrors the Lyraweb-mobile implementation).
 *
 * When `isUserPatchEnabled` is off, the legacy `submitEditProfileData` (PUT /v1/user) runs unchanged
 * — `lyraId` and any `legacyFields` are merged into the body because v1 identifies the user from the
 * body and merges non-null fields. When on, PATCH /v2/user is used (the v2 endpoint takes the id from
 * the URL and handles those legacy concerns server-side), and any failure is reported to Sentry under
 * a single `action_type` (`USER_PATCH_CUTOVER`) tag so this effort can be filtered on its own.
 *
 * `legacyFields` are v1-only fields the old body carried that the v2 patch doesn't model (e.g.
 * `username` for the Okta migration lookup); they're merged into the v1 body only.
 */
export async function updateUserWithCutover({
  isUserPatchEnabled,
  userId,
  patch,
  legacyFields,
}: {
  isUserPatchEnabled: boolean
  userId: string
  patch: UserPatch
  legacyFields?: Partial<User>
}): Promise<AxiosResponse<User>> {
  if (!isUserPatchEnabled) {
    // `UserPatch` allows `null` on its JsonNullable fields (v2 "clear the value" semantics); the v1 PUT
    // body is typed `Partial<User>` and simply merges non-null fields, so those nulls are harmless here.
    return submitEditProfileData(userId, { ...patch, ...legacyFields, lyraId: userId } as Partial<User>)
  }

  try {
    return await axiosInstance.patch<User>(`/v2/user/${userId}`, patch)
  } catch (error) {
    sentryCaptureException(error as Error, {
      type: 'USER_PATCH_CUTOVER',
      endpointKey: 'updateUser',
      requestMethod: 'PATCH',
      requestUrl: '/v2/user/:id',
      statusCode: (error as AxiosError)?.response?.status,
    })
    throw error
  }
}
